import { useEffect, useState, useMemo } from 'react';
import { useProject } from '../../hooks/useProject';
import { usePostProcessingMetrics, useCommunicationPatterns, useNamedEntities } from '../../hooks/usePostProcessingMetrics';
import { hasPermission } from '../../types/project';
import { broadcastBasicSearch } from '../../services/angular-bridge';
import { ProcessingReportModal } from './ProcessingReportModal';
import { navigateToAngular } from '../../services/navigate';
import { PieChartPanel } from '../../components/charts/PieChartPanel';
import { BarChartPanel } from '../../components/charts/BarChartPanel';
import { DocumentTimeline } from '../../components/charts/DocumentTimeline';
import { ChordDiagram } from '../../components/charts/ChordDiagram';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import { DownloadButton } from '../../components/DownloadButton';
import { SEMANTIC_ENTITY_TYPES, DEFAULT_ENTITY_TYPE_ID } from '../../constants/entityTypes';
import type { PostProcessingMetric, TagMetric } from '../../hooks/usePostProcessingMetrics';

type DataSelection = 'Extension' | 'Category' | 'Collection' | 'Custodian';

// Styles the native range to approximate the old bootstrap-slider: rounded track
// with a Bootstrap-blue fill up to the handle and a round handle. The fill width
// comes from the `--pct` CSS var set inline from the current value.
const SLIDER_CSS = `
.iio-comm-slider { -webkit-appearance: none; appearance: none; height: 20px; background: transparent; cursor: pointer; vertical-align: middle; }
.iio-comm-slider:focus { outline: none; }
.iio-comm-slider::-webkit-slider-runnable-track { height: 10px; border-radius: 5px; background: linear-gradient(#337ab7,#337ab7) 0/var(--pct,50%) 100% no-repeat, #e0e0e0; }
.iio-comm-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; height: 18px; width: 18px; margin-top: -4px; border-radius: 50%; background: #f5f5f5; border: 1px solid #adadad; box-shadow: 0 1px 2px rgba(0,0,0,.3); }
.iio-comm-slider::-moz-range-track { height: 10px; border-radius: 5px; background: #e0e0e0; }
.iio-comm-slider::-moz-range-progress { height: 10px; border-radius: 5px; background: #337ab7; }
.iio-comm-slider::-moz-range-thumb { height: 18px; width: 18px; border-radius: 50%; background: #f5f5f5; border: 1px solid #adadad; box-shadow: 0 1px 2px rgba(0,0,0,.3); }
`;

function tagMetricsToBar(tags: TagMetric[]): { label: string; value: number }[] {
  return tags.map(t => ({ label: t.value.key, value: t.value.value }));
}

interface Props {
  projectId: string;
}

export function PostProcessingTab({ projectId }: Props) {
  const { data: project, isLoading: isLoadingProject } = useProject(projectId);
  const canManageReports = project ? hasPermission(project, 'MANAGE_REPORTS') : false;
  const { data: metrics, isLoading: isLoadingMetrics } = usePostProcessingMetrics(projectId);

  const [showReport, setShowReport] = useState(false);
  const [dataSelection, setDataSelection] = useState<DataSelection>('Extension');
  const [entityType, setEntityType] = useState(
    SEMANTIC_ENTITY_TYPES.find(t => t.id === DEFAULT_ENTITY_TYPE_ID) ?? SEMANTIC_ENTITY_TYPES[0]);
  const [entityDropdownOpen, setEntityDropdownOpen] = useState(false);
  const [viewRange, setViewRange] = useState<[Date, Date] | null>(null);

  // Communication Chart slider (Angular parity: min 1, max 100, default 50).
  // sliderValue tracks the live handle; commCount commits on release and drives
  // the refetch, so we hit the endpoint once per adjustment, not per pixel.
  const [sliderValue, setSliderValue] = useState(50);
  const [commCount, setCommCount] = useState<number | null>(null);
  const { data: commPatterns } = useCommunicationPatterns(projectId, commCount ?? 50, commCount !== null);
  const activeCommunicationPattern = commPatterns?.[0] ?? metrics?.communicationPatterns?.[0];

  useEffect(() => {
    if (metrics) {
      setViewRange([new Date(metrics.startDate), new Date(metrics.endDate)]);
    }
  }, [metrics?.startDate, metrics?.endDate]);

  function formatDateSearch(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  function handleSearch(query: string) {
    broadcastBasicSearch(query);
  }

  function handleViewDocuments() {
    const [start, end] = viewRange ?? [new Date(metrics!.startDate), new Date(metrics!.endDate)];
    broadcastBasicSearch(`createdDate: [${formatDateSearch(start)} TO ${formatDateSearch(end)}]`, 1);
    navigateToAngular(`/project/${projectId}/browse`);
  }

  const collectionIdByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of (project?.collections ?? [])) map.set(c.name, c.id);
    return map;
  }, [project?.collections]);

  const collectionBySize = useMemo<PostProcessingMetric[]>(() =>
    (project?.collections ?? []).map(c => ({
      label: c.name,
      value: (c.processingSource?.bytes ?? 0) + (c.processingOutput?.bytes ?? 0),
    })), [project?.collections]);

  const collectionByCount = useMemo<PostProcessingMetric[]>(() =>
    (project?.collections ?? []).map(c => ({
      label: c.name,
      value: (c.processingSource?.files ?? 0) + (c.processingOutput?.files ?? 0),
    })), [project?.collections]);

  const custodianBySize = useMemo<PostProcessingMetric[]>(() => {
    const map = new Map<string, number>();
    for (const c of (project?.collections ?? [])) {
      const bytes = (c.processingSource?.bytes ?? 0) + (c.processingOutput?.bytes ?? 0);
      map.set(c.custodian.name, (map.get(c.custodian.name) ?? 0) + bytes);
    }
    return Array.from(map.entries()).map(([label, value]) => ({ label, value }));
  }, [project?.collections]);

  const custodianByCount = useMemo<PostProcessingMetric[]>(() => {
    const map = new Map<string, number>();
    for (const c of (project?.collections ?? [])) {
      const files = (c.processingSource?.files ?? 0) + (c.processingOutput?.files ?? 0);
      map.set(c.custodian.name, (map.get(c.custodian.name) ?? 0) + files);
    }
    return Array.from(map.entries()).map(([label, value]) => ({ label, value }));
  }, [project?.collections]);

  const sizeData =
    dataSelection === 'Extension' ? (metrics?.extensionBySize ?? []) :
    dataSelection === 'Category' ? (metrics?.typeBySize ?? []) :
    dataSelection === 'Collection' ? collectionBySize : custodianBySize;

  const countData =
    dataSelection === 'Extension' ? (metrics?.extensionByCount ?? []) :
    dataSelection === 'Category' ? (metrics?.typeByCount ?? []) :
    dataSelection === 'Collection' ? collectionByCount : custodianByCount;

  const sizeTitle =
    dataSelection === 'Extension' ? 'Extension by Size' :
    dataSelection === 'Category' ? 'Category by Size' :
    dataSelection === 'Collection' ? 'Collection by Size' : 'Custodian by Size';

  const countTitle =
    dataSelection === 'Extension' ? 'Extension by Count' :
    dataSelection === 'Category' ? 'Category by Count' :
    dataSelection === 'Collection' ? 'Collection by Count' : 'Custodian by Count';

  const emailDomains = (metrics?.emailDomains ?? []).map(d => ({ label: d.key, value: d.value }));
  const systemTags = tagMetricsToBar(metrics?.systemTags ?? []);
  const systemTagIdByLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of (metrics?.systemTags ?? [])) map.set(t.value.key, t.id);
    return map;
  }, [metrics?.systemTags]);
  // The aggregate metrics response only carries `person` entities; any other
  // type needs its own fetch.
  const isDefaultEntityType = entityType.id === DEFAULT_ENTITY_TYPE_ID;
  const { data: fetchedEntities, isFetching: isFetchingEntities } =
    useNamedEntities(projectId, entityType.id, !isDefaultEntityType);
  const entityData = tagMetricsToBar(
    isDefaultEntityType ? (metrics?.namedEntities ?? []) : (fetchedEntities ?? [])
  );

  if (isLoadingProject || isLoadingMetrics) return <LoadingPlaceholder />;

  return (
    <>
      <div className="clearfix">
        <h2 className="pull-left">
          <i className="far fa-lightbulb" aria-hidden="true" />{' '}
          {project?.name} Post-Processing Overview
        </h2>
        {canManageReports && (
          <DownloadButton onClick={() => setShowReport(true)} />
        )}
      </div>

      {/* Data Overview */}
      <div className="row">
        <h2>Data Overview</h2>
        <div className="btn-group">
          {(['Extension', 'Category', 'Collection', 'Custodian'] as DataSelection[]).map(s => (
            <label
              key={s}
              className={`btn btn-default${dataSelection === s ? ' active' : ''}`}
              onClick={() => setDataSelection(s)}
            >
              {s}
            </label>
          ))}
        </div>

        {(sizeData.length > 0 || countData.length > 0) && (
          <div style={{ marginTop: 44 }}>
            <PieChartPanel key={`size-${dataSelection}`} title={sizeTitle} data={sizeData} valueType="bytes"
              onItemClick={label => handleSearch(
                dataSelection === 'Extension' ? `fileExtension:"${label}"` :
                dataSelection === 'Category' ? `category:"${label}"` :
                dataSelection === 'Collection' ? `collectionId:"${collectionIdByName.get(label) ?? label}"` :
                `custodian:"${label}"`
              )} />
            <div className="col-md-2" />
            <PieChartPanel key={`count-${dataSelection}`} title={countTitle} data={countData} valueType="count"
              onItemClick={label => handleSearch(
                dataSelection === 'Extension' ? `fileExtension:"${label}"` :
                dataSelection === 'Category' ? `category:"${label}"` :
                dataSelection === 'Collection' ? `collectionId:"${collectionIdByName.get(label) ?? label}"` :
                `custodian:"${label}"`
              )} />
          </div>
        )}
      </div>

      {/* Document Timeline */}
      <div className="row" style={{ marginTop: 96 }}>
        <div style={{ display: 'table' }}>
          <div style={{ display: 'table-cell' }}>
            <h2>Document Timeline [</h2>
          </div>
          <div style={{ display: 'table-cell', verticalAlign: 'middle', paddingTop: 15 }}>
            <a onClick={handleViewDocuments} style={{ cursor: 'pointer' }}>
              View Documents in Range
            </a>
          </div>
          <div style={{ display: 'table-cell' }}>
            <h2>]</h2>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12" style={{ paddingRight: 20 }}>
            {metrics && (metrics.documentCountByDate ?? []).length > 0 ? (
              <DocumentTimeline
                data={metrics.documentCountByDate}
                onRangeChange={(start, end) => setViewRange([start, end])}
              />
            ) : (
              <p className="text-muted">No document date data available.</p>
            )}
          </div>
        </div>
      </div>

      {/* Email Domains */}
      {emailDomains.length > 0 && (
        <div className="row" style={{ marginTop: 96 }}>
          <h2>Email Domains</h2>
          <div className="row">
            <div className="col-md-12">
              <BarChartPanel key="emailDomains" data={emailDomains}
                onItemClick={label => handleSearch(`emailDomains:"${label}"`)} />
            </div>
          </div>
        </div>
      )}

      {/* System Tags */}
      {systemTags.length > 0 && (
        <div className="row" style={{ marginTop: 96 }}>
          <h2>System Tags</h2>
          <div className="row">
            <div className="col-md-12">
              <BarChartPanel key="systemTags" data={systemTags}
                onItemClick={label => handleSearch(`systemTags:"${systemTagIdByLabel.get(label) ?? label}"`)} />
            </div>
          </div>
        </div>
      )}

      {/* Named Entities */}
      <div className="row" style={{ marginTop: 96 }}>
        <div>
          <h2>Named Entities</h2>
          <div className={`btn-group${entityDropdownOpen ? ' open' : ''}`}>
            <button
              className="btn btn-default"
              onClick={() => setEntityDropdownOpen(o => !o)}
            >
              {entityType.name} <span className="caret" />
            </button>
            <ul className="dropdown-menu">
              {SEMANTIC_ENTITY_TYPES.map(t => (
                <li key={t.id}>
                  <a onClick={() => { setEntityType(t); setEntityDropdownOpen(false); }}>
                    {t.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            {entityData.length > 0 ? (
              <BarChartPanel key={`entities-${entityType.id}`} data={entityData}
                onItemClick={label => handleSearch(`${entityType.field}Entities.lowercase:"${label}"`)} />
            ) : isFetchingEntities ? (
              <p className="text-muted">Loading {entityType.name.toLowerCase()} entities…</p>
            ) : (
              <p className="text-muted">No {entityType.name.toLowerCase()} entities found.</p>
            )}
          </div>
        </div>
      </div>

      {activeCommunicationPattern && (
        <div className="row" style={{ marginTop: 96 }}>
          <div>
            <h2>Communication Chart</h2>
            <style>{SLIDER_CSS}</style>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                className="iio-comm-slider"
                type="range"
                min={1}
                max={100}
                step={1}
                value={sliderValue}
                onChange={e => setSliderValue(Number(e.target.value))}
                onPointerUp={() => setCommCount(sliderValue)}
                onKeyUp={() => setCommCount(sliderValue)}
                style={{ width: 270, ['--pct' as string]: `${((sliderValue - 1) / 99) * 100}%` }}
                aria-label="Number of communicators"
              />
              <span className="text-muted">Top {sliderValue} Senders</span>
            </div>
            <ChordDiagram
              pattern={activeCommunicationPattern}
              onGroupClick={(group) =>
                broadcastBasicSearch(`emailFrom:"${group}" OR emailRecipients: "${group}"`)
              }
            />
          </div>
        </div>
      )}
      {showReport && (
        <ProcessingReportModal projectId={projectId} projectName={project?.name ?? ''} type="post_processing_report"
          onClose={() => setShowReport(false)} />
      )}
    </>
  );
}
